import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { FLAT_NAV } from "@/components/Layout";
import { MagnifyingGlass, ArrowElbowDownLeft } from "@phosphor-icons/react";

const ACTIONS = [
  { label: "Import items from Xero", hint: "Month-end", to: "/unscheduled", kw: "import sync xero fetch" },
  { label: "New month-end close", hint: "Month-end", to: "/close", kw: "close period sign off lock" },
  { label: "Review approvals", hint: "Month-end", to: "/approvals", kw: "approve journals two person review" },
  { label: "New adjustment", hint: "Ledgers", to: "/accruals", kw: "manual adjustment accrual" },
  { label: "Run the forecast", hint: "Reports", to: "/forecast", kw: "forecast 12 month projection" },
];

export default function CommandPalette() {
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [idx, setIdx] = useState(0);
  const inputRef = useRef(null);

  const close = useCallback(() => { setOpen(false); setQ(""); setIdx(0); }, []);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === "Escape" && open) {
        close();
      }
    };
    window.addEventListener("keydown", onKey);
    const onOpen = () => setOpen(true);
    window.addEventListener("command-palette:open", onOpen);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("command-palette:open", onOpen);
    };
  }, [open, close]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 30); }, [open]);

  const groups = useMemo(() => {
    const term = q.trim().toLowerCase();
    const navItems = FLAT_NAV.map((n) => ({ label: n.label, to: n.to, icon: n.icon, hint: "Go to" }));
    const all = [...ACTIONS, ...navItems];
    const filtered = term
      ? all.filter((i) => (`${i.label} ${i.hint} ${i.kw || ""}`).toLowerCase().includes(term))
      : all;
    return filtered.slice(0, 12);
  }, [q]);

  useEffect(() => { setIdx(0); }, [q]);

  const choose = (item) => { if (!item) return; close(); nav(item.to); };

  const onKeyDown = (e) => {
    if (e.key === "ArrowDown") { e.preventDefault(); setIdx((i) => Math.min(i + 1, groups.length - 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setIdx((i) => Math.max(i - 1, 0)); }
    else if (e.key === "Enter") { e.preventDefault(); choose(groups[idx]); }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-start justify-center pt-[12vh] px-4 bg-navy/40 backdrop-blur-sm"
      onMouseDown={close} data-testid="command-palette">
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden fade-up"
        onMouseDown={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 px-4 border-b border-slate-100">
          <MagnifyingGlass size={18} className="text-navy/65 shrink-0" />
          <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={onKeyDown}
            data-testid="command-palette-input" placeholder="Search pages and actions…"
            className="w-full py-3.5 text-[0.95rem] text-navy placeholder:text-navy/35 outline-none bg-transparent" />
          <kbd className="text-[0.6rem] font-mono text-navy/65 border border-slate-200 rounded px-1.5 py-0.5 shrink-0">ESC</kbd>
        </div>
        <div className="max-h-[52vh] overflow-y-auto py-2 sidebar-scroll">
          {groups.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-navy/65" data-testid="command-palette-empty">No matches.</div>
          )}
          {groups.map((item, i) => {
            const Icon = item.icon;
            return (
              <button key={`${item.label}-${i}`} data-testid={`command-item-${i}`}
                onMouseEnter={() => setIdx(i)} onClick={() => choose(item)}
                className={`w-full flex items-center gap-3 px-4 py-2.5 text-left transition-colors ${
                  i === idx ? "bg-orange/[0.08]" : "hover:bg-slate-50"}`}>
                {Icon ? <Icon size={17} className="text-navy/70 shrink-0" /> : <span className="w-[17px]" />}
                <span className="flex-1 text-sm text-navy">{item.label}</span>
                <span className="text-[0.62rem] uppercase tracking-wider text-navy/35">{item.hint}</span>
                {i === idx && <ArrowElbowDownLeft size={14} className="text-orangeink" />}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
